import {
  RocketIcon,
  ArrowUpRightIcon,
  CommentIcon,
  ArrowSwitchIcon,
  HeartIcon,
  HeartFillIcon,
} from '@primer/octicons-react'
import {ActionMenu, ActionList} from '@primer/react'
import {clsx} from 'clsx'
import {useState} from 'react'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {useToggleFeaturePreview} from '../../../hooks/use-toggle-feature-preview'
import {useLayoutRouteData} from '../../../page-data/loaders/use-layout-route-data'
import {usePullRequestAnalytics} from '../../../hooks/use-pull-request-analytics'
import {NewLabel} from '@github-ui/lifecycle-labels/new'
import {AlphaLabel} from '@github-ui/lifecycle-labels/alpha'
import {sendCustomMetric, isStaff} from '@github-ui/stats'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import styles from './OverviewFeedback.module.css'

const LOVED_STORAGE_KEY = 'pull-requests.overview.loved-feature'

export function OverviewFeedback() {
  const {urls} = useLayoutRouteData()
  const toggleFeature = useToggleFeaturePreview('prs_overview_react')
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  const [loved, setLoved] = useLocalStorage(LOVED_STORAGE_KEY, false)
  const [animateLove, setAnimateLove] = useState(false)
  const {isBrowser} = useRuntimeEnvironment()
  const staff = isBrowser && isStaff()

  const handleToggleOff = async () => {
    sendPullRequestAnalyticsEvent('overview.feature_toggle', 'OVERVIEW_FEATURE_TOGGLE', {enabled: false})
    await toggleFeature()
    window.location.href = `${urls.conversation}?overview=false`
  }

  const handleLove = () => {
    const nextLoved = !loved
    setLoved(nextLoved)
    setAnimateLove(nextLoved)

    if (nextLoved) {
      sendCustomMetric({name: 'PULL_REQUESTS_OVERVIEW_LOVE_THIS_FEATURE', value: 1})
      sendPullRequestAnalyticsEvent('overview.love_this_feature', 'OVERVIEW_LOVE_THIS_FEATURE')
    } else {
      sendCustomMetric({name: 'PULL_REQUESTS_OVERVIEW_UNLOVE_THIS_FEATURE', value: 1})
      sendPullRequestAnalyticsEvent('overview.unlove_this_feature', 'OVERVIEW_UNLOVE_THIS_FEATURE')
    }
  }

  return (
    <ActionMenu>
      <ActionMenu.Button
        variant="invisible"
        size="small"
        onClick={() => setAnimateLove(false)}
        aria-label={staff ? 'Staff: Overview preview options' : 'Private preview: Overview preview options'}
      >
        {staff ? <NewLabel>Staff</NewLabel> : <AlphaLabel>Private preview</AlphaLabel>}
      </ActionMenu.Button>
      <ActionMenu.Overlay>
        <ActionList>
          <ActionList.LinkItem href="https://gh.io/pr-overview" target="_blank" rel="noopener noreferrer">
            <ActionList.LeadingVisual>
              <RocketIcon />
            </ActionList.LeadingVisual>
            What&apos;s new
            <ActionList.TrailingVisual>
              <ArrowUpRightIcon />
            </ActionList.TrailingVisual>
          </ActionList.LinkItem>
          <ActionList.LinkItem href="https://gh.io/pr-overview-feedback" target="_blank" rel="noopener noreferrer">
            <ActionList.LeadingVisual>
              <CommentIcon />
            </ActionList.LeadingVisual>
            Give feedback
            <ActionList.TrailingVisual>
              <ArrowUpRightIcon />
            </ActionList.TrailingVisual>
          </ActionList.LinkItem>
          <ActionList.Item
            role="menuitemcheckbox"
            aria-checked={loved}
            onSelect={event => {
              event.preventDefault()
              handleLove()
            }}
          >
            <ActionList.LeadingVisual
              className={clsx(loved && styles.lovedHeart, animateLove && styles.loveAnimation)}
              onAnimationEnd={() => setAnimateLove(false)}
            >
              {loved ? <HeartFillIcon /> : <HeartIcon />}
            </ActionList.LeadingVisual>
            <span>{loved ? 'Loved!' : 'Love this feature'}</span>
          </ActionList.Item>
          <ActionList.Divider />
          <ActionList.Item onSelect={handleToggleOff}>
            <ActionList.LeadingVisual>
              <ArrowSwitchIcon />
            </ActionList.LeadingVisual>
            Turn off Overview preview
          </ActionList.Item>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
